import { createFileRoute, Link } from '@tanstack/react-router';
import type { ComponentProps, FormEvent } from 'react';
import { useState } from 'react';
import type { LeadStatus, TrialStatus } from '@pirouette/shared';
import { leadStatusSchema } from '@pirouette/shared';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useCreateTrialBooking, useLead, useUpdateLeadStatus } from '@/data/leads';

export const Route = createFileRoute('/admin/leads/$id')({
  component: LeadDetail,
});

const statusLabels: Record<LeadStatus, string> = {
  inquiry: 'Inquiry',
  trial_booked: 'Trial booked',
  trial_attended: 'Trial attended',
  consult_needed: 'Consult needed',
  waiver_pending: 'Waiver pending',
  payment_pending: 'Payment pending',
  enrolled: 'Enrolled',
  lost: 'Lost',
};

const statusTones: Record<LeadStatus, ComponentProps<typeof Badge>['tone']> = {
  inquiry: 'info',
  trial_booked: 'accent',
  trial_attended: 'success',
  consult_needed: 'warn',
  waiver_pending: 'warn',
  payment_pending: 'warn',
  enrolled: 'success',
  lost: 'neutral',
};

const trialStatusLabels: Record<TrialStatus, string> = {
  booked: 'Booked',
  attended: 'Attended',
  missed: 'Missed',
  cancelled: 'Cancelled',
  converted: 'Converted',
};

function formatDateOnly(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

function LeadDetail() {
  const { id } = Route.useParams();
  const lead = useLead(id);
  const updateStatus = useUpdateLeadStatus();
  const createTrial = useCreateTrialBooking();
  const [trialDate, setTrialDate] = useState('');
  const [trialNotes, setTrialNotes] = useState('');

  if (lead.isLoading) return <p aria-live="polite">Loading…</p>;
  if (lead.isError || !lead.data) {
    return (
      <p role="alert" className="text-[var(--color-danger)]">
        Lead not found.
      </p>
    );
  }

  const currentLead = lead.data;

  const onCreateTrial = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await createTrial.mutateAsync({
      lead_id: currentLead.id,
      program_id: currentLead.desired_program_id ?? undefined,
      trial_date: trialDate,
      notes: trialNotes || undefined,
    });
    setTrialDate('');
    setTrialNotes('');
  };

  return (
    <div className="space-y-10">
      <div>
        <Link
          to="/admin/leads"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Leads
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1>{currentLead.guardian_name}</h1>
          <Badge tone={statusTones[currentLead.lead_status]}>
            {statusLabels[currentLead.lead_status]}
          </Badge>
        </div>
        <p className="mt-1 text-[var(--color-ink-muted)]">
          {currentLead.dancer_name} · {currentLead.guardian_email}
        </p>
      </div>

      <section aria-labelledby="lead-details-heading" className="surface-elevated p-6">
        <h2 id="lead-details-heading" className="font-medium text-[var(--text-lg)]">
          Lead details
        </h2>
        <dl className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Phone</dt>
            <dd className="mt-1 font-medium">{currentLead.guardian_phone ?? 'Not provided'}</dd>
          </div>
          <div>
            <dt className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Desired program</dt>
            <dd className="mt-1 font-medium">
              {currentLead.program_offerings?.name ?? 'Not selected'}
            </dd>
          </div>
          <div>
            <dt className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Source</dt>
            <dd className="mt-1 font-medium">{currentLead.source ?? 'Unknown'}</dd>
          </div>
        </dl>
        {currentLead.notes && (
          <p className="mt-5 text-[var(--color-ink-muted)]">{currentLead.notes}</p>
        )}
        {currentLead.lost_reason && (
          <p className="mt-3 text-[var(--color-ink-muted)]">
            Lost reason: {currentLead.lost_reason}
          </p>
        )}
      </section>

      <section aria-labelledby="status-heading" className="space-y-4">
        <h2 id="status-heading" className="text-[var(--text-xl)]">
          Status
        </h2>
        <div className="surface flex flex-wrap gap-2 p-4">
          {leadStatusSchema.options.map((status) => (
            <Button
              key={status}
              size="sm"
              variant={status === currentLead.lead_status ? 'primary' : 'secondary'}
              disabled={updateStatus.isPending || status === currentLead.lead_status}
              onClick={() => updateStatus.mutate({ id: currentLead.id, status })}
            >
              {statusLabels[status]}
            </Button>
          ))}
        </div>
      </section>

      <section aria-labelledby="trial-heading" className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div>
          <h2 id="trial-heading" className="text-[var(--text-xl)]">
            Trial bookings
          </h2>
          {(currentLead.trial_bookings?.length ?? 0) === 0 && (
            <p className="mt-3 text-[var(--color-ink-muted)]">No trial bookings yet.</p>
          )}
          {currentLead.trial_bookings && currentLead.trial_bookings.length > 0 && (
            <ul className="mt-4 grid gap-2">
              {currentLead.trial_bookings.map((booking) => (
                <li
                  key={booking.id}
                  className="surface flex flex-wrap items-center justify-between gap-3 p-4"
                >
                  <div>
                    <p className="font-medium">{formatDateOnly(booking.trial_date)}</p>
                    {booking.notes && (
                      <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                        {booking.notes}
                      </p>
                    )}
                  </div>
                  <Badge>{trialStatusLabels[booking.status]}</Badge>
                </li>
              ))}
            </ul>
          )}
        </div>

        <form
          onSubmit={(event) => {
            void onCreateTrial(event);
          }}
          className="surface-elevated space-y-4 p-5"
        >
          <h3 className="font-medium text-[var(--text-base)]">Book trial</h3>
          <label className="block text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            Trial date
            <input
              type="date"
              required
              value={trialDate}
              onChange={(event) => setTrialDate(event.target.value)}
              className="mt-1 block h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-[var(--color-ink)]"
            />
          </label>
          <label className="block text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            Notes
            <textarea
              value={trialNotes}
              onChange={(event) => setTrialNotes(event.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[var(--color-ink)]"
            />
          </label>
          <Button type="submit" disabled={createTrial.isPending || !trialDate} className="w-full">
            Create booking
          </Button>
        </form>
      </section>
    </div>
  );
}
